import React, { useEffect, useState } from "react";
import {
  Box, Typography, TextField, Button, Grid,
  FormControl, InputLabel, Select, MenuItem,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";
import {
  fetchBusinessUnit,
  fetchBusinessDomain,
  updateBusinessUnit,
} from "../../api/companyApi";
import { useNotification } from "../../providers/NotificationProvider";

/** ISO date string → "YYYY-MM-DD" for date input */
const toDateInput = (iso) => {
  if (!iso) return "";
  return iso.substring(0, 10);
};

export default function BusinessUnitEditPage() {
  const { businessDomainId, businessUnitId } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const t = (id) => intl.formatMessage({ id });

  // APIから取得した全フィールドをそのまま保持する（industryId/industryName等を失わないため）
  const [original, setOriginal] = useState(null);
  const [isEnterprise, setIsEnterprise] = useState(false);
  const [form, setForm] = useState({
    businessUnitName: "",
    vision: "",
    startDate: "",
    endDate: "",
    url: "",
    businessState: "Planned",
  });

  useEffect(() => {
    (async () => {
      try {
        const d = await fetchBusinessUnit(businessUnitId);
        setOriginal(d);
        setForm({
          businessUnitName: d.businessUnitName || "",
          vision: d.vision || "",
          startDate: toDateInput(d.startDate),
          endDate: toDateInput(d.endDate),
          url: d.url || "",
          businessState: d.businessState || "Planned",
        });
        // enterprise ドメインかどうかを確認（enterprise BU はステータス変更不可）
        try {
          const domain = await fetchBusinessDomain(d.businessDomainId);
          setIsEnterprise(domain.enterprise === true);
        } catch (e) { /* enterprise 判定失敗時はデフォルト false のまま */ }
      } catch (e) { notify.apiError(e); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessUnitId]);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!original) return;
    try {
      // 取得した全フィールドをベースに、ユーザーが編集したフィールドだけ上書き
      await updateBusinessUnit({
        ...original,
        businessUnitName: form.businessUnitName,
        vision: form.vision,
        startDate: form.startDate || null,
        endDate: form.endDate || null,
        url: form.url || null,
        businessState: form.businessState,
      });
      notify.success(t("message.update.success"));
      navigate(`/strategy/${businessDomainId}/business-units/${businessUnitId}`);
    } catch (err) { notify.apiError(err); }
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>{t("businessUnit.edit.title")}</Typography>
      <Box component="form" onSubmit={handleSubmit}>
        <Grid container spacing={2}>

          {/* 事業ドメイン（表示のみ） */}
          <Grid size={12} md={6}>
            <TextField
              fullWidth disabled
              label={t("businessDomain.title")}
              value={original?.businessDomainName || ""}
            />
          </Grid>

          {/* 事業単位名 */}
          <Grid size={12} md={6}>
            <TextField
              fullWidth required
              label={t("businessUnit.name")}
              name="businessUnitName"
              value={form.businessUnitName}
              onChange={handleChange}
            />
          </Grid>

          {/* ビジョン */}
          <Grid size={12}>
            <TextField
              fullWidth multiline rows={2}
              label={t("businessUnit.vision")}
              name="vision"
              value={form.vision}
              onChange={handleChange}
            />
          </Grid>

          {/* 開始日 */}
          <Grid size={12} md={6}>
            <TextField
              fullWidth
              type="date"
              label={t("businessUnit.startDate")}
              name="startDate"
              value={form.startDate}
              onChange={handleChange}
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Grid>

          {/* 終了日 */}
          <Grid size={12} md={6}>
            <TextField
              fullWidth
              type="date"
              label={t("businessUnit.endDate")}
              name="endDate"
              value={form.endDate}
              onChange={handleChange}
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Grid>

          {/* URL */}
          <Grid size={12} md={6}>
            <TextField
              fullWidth
              label={t("businessUnit.url")}
              name="url"
              value={form.url}
              onChange={handleChange}
            />
          </Grid>

          {/* ステータス（enterprise ドメインの BU は変更不可） */}
          <Grid size={12} md={6}>
            <FormControl fullWidth disabled={isEnterprise}>
              <InputLabel>{t("dataCategory.businessState")}</InputLabel>
              <Select
                name="businessState"
                value={form.businessState}
                onChange={handleChange}
                label={t("dataCategory.businessState")}
              >
                <MenuItem value="Planned">{t("businessState.Planned")}</MenuItem>
                <MenuItem value="Executing">{t("businessState.Executing")}</MenuItem>
                <MenuItem value="Completed">{t("businessState.Completed")}</MenuItem>
              </Select>
            </FormControl>
          </Grid>

        </Grid>
        <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
          <Button variant="contained" type="submit">{t("common.save")}</Button>
          <Button variant="outlined" onClick={() => navigate(`/strategy/${businessDomainId}/business-units/${businessUnitId}`)}>{t("common.cancel")}</Button>
        </Box>
      </Box>
    </Box>
  );
}
